import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (err) {
      if (err?.response?.status && err.response.status !== 401) {
        console.warn("Auth refresh error:", err?.message || err);
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from Emergent OAuth, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    // Accept both fragment (#session_id=) and query (?session_id=) — some
    // iOS PWAs strip the fragment.
    if (typeof window !== "undefined") {
      const hasHash = window.location.hash?.includes("session_id=");
      const hasQuery = window.location.search?.includes("session_id=");
      if (hasHash || hasQuery) {
        setLoading(false);
        return;
      }
    }
    refresh();
  }, [refresh]);

  const login = useCallback(async (phone, password) => {
    const { data } = await api.post("/auth/login", { phone, password });
    // JWT lives in an HttpOnly cookie set by the backend — never touch localStorage
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      console.warn("Logout error (safe to ignore):", err?.message || err);
    }
    // Legacy cleanup for users who upgraded from the localStorage era
    try { localStorage.removeItem("bkpro_token"); } catch (e) { console.debug("[auth] localStorage cleanup blocked:", e?.message || e); }
    setUser(null);
  }, []);

  const isAdmin = user?.role === "admin" || user?.role === "compta" || user?.role === "associate";
  const permissions = useMemo(() => new Set(user?.permissions || []), [user]);
  const has = useCallback((perm) => permissions.has(perm), [permissions]);

  // `isAdmin`, `permissions` and `has` are all derived from `user` — no need
  // to list them separately, `user` alone triggers the correct re-memoization.
  const value = useMemo(
    () => ({ user, loading, login, logout, refresh, setUser, isAdmin, permissions, has }),
    [user, loading, login, logout, refresh, isAdmin, permissions, has]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
